<template>
  <div class="relative">
    <div class="relative">
      <div class="relative">
        <slot name="wrapper" />
        <div
          ref="emblaRef"
          class="embla"
          :class="clip ? 'overflow-hidden' : 'overflow-x-clip overflow-y-visible'"
        >
          <div
            class="embla__container flex overflow-visible"
            :class="ui.wrapper?.class"
            :style="ui.wrapper?.style"
          >
            <div
              v-for="(slide, index) in items"
              :key="index"
              class="embla__slide relative grow-0 shrink-0"
              :style="ui.slide?.style"
              :class="ui.slide?.class"
            >
              <slot name="items" :item="slide" :index="index" />
            </div>
          </div>
        </div>
      </div>
      <slot name="indicators" :api="emblaApi" :active="active" />
    </div>
    <slot name="footer" />
  </div>
</template>

<script setup lang="ts" generic="T extends object">
import emblaCarouselVue from 'embla-carousel-vue'
import Autoplay from 'embla-carousel-autoplay'

const props = withDefaults(
  defineProps<{
    items: T[]
    loop?: boolean
    ui?: {
      slide?: {
        class?: string | string[]
        style?: string | Record<string, string>[]
      }
      wrapper?: {
        class?: string | string[]
        style?: string | Record<string, string>[]
      }
    }
    autoPlayActive?: boolean
    // false = vypne embla swipe (drag). V editoru je nutné, jinak by embla během tažení
    // bloku uvnitř slajdu chytila pointer a slider by odjel.
    watchDrag?: boolean
    // false (edit mód) = .embla clipuje jen VODOROVNĚ (overflow-x: clip), aby sousední snímky
    // nekoukaly ven, ale svisle přetéká (overflow-y: visible) → toolbary/handly bloků trčící nad
    // snímek zůstanou vidět. `clip` (na rozdíl od `hidden`) NEnutí druhou osu do auto/scroll, takže
    // kombinace clip + visible je stabilní; `overflow-x: hidden` by naopak overflow-y srazil na auto
    // a toolbary zase ořízl. true (view mód / přechod) = overflow-hidden na obou osách jako klasický slider.
    clip?: boolean
  }>(),
  {
    loop: false,
    ui: () => ({
      slide: { class: 'basis-full' },
      wrapper: {},
    }),
    autoPlayActive: true,
    watchDrag: true,
    clip: true,
  }
)
const [emblaRef, emblaApi] = emblaCarouselVue({ loop: props.loop, watchDrag: props.watchDrag }, [
  Autoplay({ active: props.autoPlayActive, delay: 10000 }),
])

// watchDrag i autoplay přepínáme runtime podle edit módu (obojí se jinak čte jen při initu).
// reInit přenastaví swipe; autoplay plugin explicitně stop/play — v edit módu tak sám vypne.
watch([() => props.watchDrag, () => props.autoPlayActive], ([watchDrag, autoPlayActive]) => {
  emblaApi.value?.reInit({ loop: props.loop, watchDrag })
  const autoplay = (
    emblaApi.value?.plugins() as { autoplay?: { play: () => void; stop: () => void } } | undefined
  )?.autoplay
  if (autoplay) {
    if (autoPlayActive) autoplay.play()
    else autoplay.stop()
  }
})
const active: Ref = ref(0)

// Během přechodu na jiný snímek musí embla clipovat (jinak by odjíždějící snímky byly vidět);
// v klidu povolíme overflow, aby se ukázaly toolbary/handly bloků nad snímkem. Timeout je
// pojistka: reInit (přepnutí watchDrag/autoplay) vystřelí pohyb bez následného `settle`, jinak
// by `sliding` zůstalo natrvalo true a overflow by zůstal skrytý.
const sliding = ref(false)
let slidingTimer: ReturnType<typeof setTimeout> | undefined
const startSliding = () => {
  sliding.value = true
  clearTimeout(slidingTimer)
  slidingTimer = setTimeout(() => (sliding.value = false), 600)
}
const stopSliding = () => {
  clearTimeout(slidingTimer)
  sliding.value = false
}
watchEffect((onCleanup) => {
  const api = emblaApi.value
  if (!api) return
  const onSelect = (a: typeof api) => {
    active.value = a.selectedScrollSnap()
    startSliding()
  }
  api.on('select', onSelect).on('settle', stopSliding).on('pointerDown', startSliding)
  onCleanup(() => {
    api.off('select', onSelect).off('settle', stopSliding).off('pointerDown', startSliding)
  })
})
onBeforeUnmount(() => clearTimeout(slidingTimer))

/** `key` je jméno vlastnosti položky, takže se z generického `T` nedá odvodit. */
const readItemKey = (item: T, key: string): unknown =>
  key in item ? (item as Record<string, unknown>)[key] : undefined

const setActive = (value: string | number, key: string = 'id', jump: boolean = true) => {
  const index = props.items.findIndex((item) => readItemKey(item, key) == value)
  emblaApi.value?.scrollTo(index, jump)
}

defineExpose({
  setActive,
  emblaApi,
})
</script>
